// Changelog markdown lives in the repo-root `content/changelog` directory (see the
// `@changelog` Vite alias), also used as the GitHub Release body. Entries expose
// the raw markdown body (frontmatter stripped); the page renders it with the
// library's `MarkdownView` (raw HTML is shown as text, never as DOM).

const CHANGELOG_LOCALES = ["en", "pt-BR"] as const;
type ChangelogLocale = (typeof CHANGELOG_LOCALES)[number];

export interface ChangelogEntry {
  version: string;
  date: Date;
  locale: ChangelogLocale;
  /** Raw markdown for the entry body (frontmatter stripped). */
  body: string;
}

interface ParsedFrontmatter {
  version: string;
  date: string;
  locale?: string;
}

const rawEntries = import.meta.glob("@changelog/**/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;

function parseFrontmatter(raw: string): { data: ParsedFrontmatter; body: string } | null {
  const match = raw.match(FRONTMATTER_RE);
  if (!match) return null;

  const [, frontmatter, body] = match;
  const data: Record<string, string> = {};
  for (const line of frontmatter.split(/\r?\n/)) {
    const separator = line.indexOf(":");
    if (separator === -1) continue;
    const key = line.slice(0, separator).trim();
    const value = line
      .slice(separator + 1)
      .trim()
      .replace(/^["']|["']$/g, "");
    if (key) data[key] = value;
  }

  if (!data.version || !data.date) return null;
  return { data: data as unknown as ParsedFrontmatter, body: body ?? "" };
}

function isChangelogLocale(value: string | undefined): value is ChangelogLocale {
  return value !== undefined && (CHANGELOG_LOCALES as readonly string[]).includes(value);
}

/**
 * Parses a `YYYY-MM-DD` frontmatter date as a local calendar date (not UTC
 * midnight), so it renders as the authored day regardless of the reader's
 * timezone offset from UTC.
 */
function parseCalendarDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return new Date(value);
  const [, year, month, day] = match;
  return new Date(Number(year), Number(month) - 1, Number(day));
}

// Parse every markdown file once; keep every localized variant keyed by version.
const localizedByVersion = new Map<string, Partial<Record<ChangelogLocale, ChangelogEntry>>>();

for (const raw of Object.values(rawEntries)) {
  const parsed = parseFrontmatter(raw);
  if (!parsed) continue;

  const locale = isChangelogLocale(parsed.data.locale) ? parsed.data.locale : "en";
  const entry: ChangelogEntry = {
    version: parsed.data.version,
    date: parseCalendarDate(parsed.data.date),
    locale,
    body: parsed.body.trim(),
  };

  const variants = localizedByVersion.get(entry.version) ?? {};
  variants[locale] = entry;
  localizedByVersion.set(entry.version, variants);
}

function compareVersionsDesc(a: string, b: string): number {
  const pa = a.split(".").map((part) => Number.parseInt(part, 10) || 0);
  const pb = b.split(".").map((part) => Number.parseInt(part, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return db - da;
  }
  return 0;
}

/** Changelog entries for the given locale (falling back to `en`), newest first. */
export function getChangelogEntries(locale: string): ChangelogEntry[] {
  const resolved: ChangelogLocale = isChangelogLocale(locale) ? locale : "en";
  return Array.from(localizedByVersion.values())
    .map((variants) => variants[resolved] ?? variants.en)
    .filter((entry): entry is ChangelogEntry => Boolean(entry))
    .sort((a, b) => compareVersionsDesc(a.version, b.version));
}
